// utils/changeCard/compat.js
//
// CHANGE CARD — WHICH ELEMENT MAPS TO WHICH (KPR-698, the first item of
// KPE-028). A person moves an item, a selection or a whole card onto another
// card by mapping each SOURCE element to a TARGET element. This table is the
// one answer to "may this source fill that target?", for the mapping utility
// (keepr-web, KPR-707), the dry run (KPR-700) and the move itself (KPR-702):
//
//   direct     the stored value is written as it is. It is still held to the
//              target's own rules (a length, bounds, a pattern, the currencies
//              it allows), and a value that breaks one is a COUNTED failure.
//   converts   a named conversion changes the value's shape or spelling (a
//              date and time becomes a date, a number gains a unit, a choice
//              is mapped value by value, anything becomes text).
//   refused    never offered, with a sentence naming both elements.
//
// What a value BECOMES — and every failure, counted, never thrown, never
// truncated or rounded silently — is utils/changeCard/convert.js, which asks
// this table first. That module is the API's alone: it needs the unit,
// currency, date and text rules, whose web copies live at other paths. The
// web needs only this table, to grey out the pairs a person cannot drop.
//
// DEPENDENCY-FREE AND PLATFORM-FREE ON PURPOSE. This file is vendored
// byte-identical into keepr-web (`src/utils/changeCard/`) with the vector file
// beside it (`vectors.json`), which both repos' suites drive, and
// `test/vendoredParity.test.js` compares the two mains. Nothing here may
// require() anything, and there is NO SPREAD SYNTAX: Babel would inject a
// helper import for it, turning this CommonJS file into an ES module with no
// default export in the web build (knowledge/history/calculated-elements.md).
// The unit and currency registries are NOT consulted: a measure is compared by
// its id (the registry's getMeasure is an exact-id lookup, so two ids that
// differ name two measures), and whether a chosen unit or currency is valid is
// convert.js's checkMapping.
//
// THE RULES (the epic's proposed matrix, with the differences the item's Plan
// states, each marked ⚑ for the owner):
//
//   any value -> short, long or rich text   converts (rendered as a person
//       reads it: a choice its label, an item lookup its title, a person
//       their name, an amount or a measurement formatted); text -> text is
//       direct, and rich text keeps its markdown. A list is joined.
//       Not a file (⚑: an unmapped file stays on the item as an attachment,
//       the epic's rule, so nothing is lost by leaving it), a count of
//       confirmations or a repeating group.
//   number · decimal · whole number -> each other, and to and from a rating
//       (⚑)                                 direct (the target's decimals,
//       bounds and a rating's steps are checked, never rounded); a rating
//       never crosses with a unit or money
//   number -> measurement / measurement -> number    converts, with a UNIT
//       chosen once for the mapping
//   measurement -> measurement              same measure only (⚑); direct, or
//       converts into the target's default unit where the target allows only
//       some units. An element's OWN units (KPR-1109) a target lacks are
//       mapped unit by unit (a UNIT MAP, `needs: units`), as choices are
//       mapped through a choice map
//   number -> currency / currency -> number          converts, with a CURRENCY
//       chosen once for the mapping (⚑ currency -> number keeps only values in
//       that currency; the others are counted failures, never mixed)
//   currency -> currency                    direct (a value in a currency the
//       target does not allow is a counted failure; no exchange rates)
//   currency <-> measurement                refused (⚑ money is not a quantity)
//   date <-> date and time                  converts (a date and time becomes
//       the collection's day; a date becomes midnight on the collection's
//       clock ⚑)
//   choice -> choice                        converts, value by value through a
//       choice map; a source choice left out of the map is counted
//   item lookup -> item lookup              direct when the target can point at
//       the source's card: the same card, or one the source's card sits
//       beneath (ctx.reaches)
//   short or long text -> link, email, phone      converts through the
//       target's own rule (⚑; failures counted). Rich text does not.
//   yes/no, time, link, phone, email, location, person, color, file
//       -> the same type only               direct
//   count of confirmations, repeating group       refused both ways (⚑)
//
// LISTS. A choice, a date, a date and time, an item lookup, a person and a
// file may hold several values ("Allow multiple"). A list maps to a list
// (`each`), one value to a list (`wrap`), and a list to one value only when
// the target is text (`join`); otherwise it is refused.
//
// TARGETS AND SOURCES. A driven, calculated or numbered (sequence) element is
// never offered as a target, and classify refuses one: keepr writes it. Neither is a count of
// confirmations or a repeating group. An element hidden from the caller is
// never offered as either, and is left out without a reason (a reason would
// name it); one the caller can see but not change is never a target.

'use strict';

// ---------------------------------------------------------------- the types

// dataType -> its family (which rules it follows), whether "Allow multiple"
// makes it a list, and how a sentence names it.
var TYPES = {
	'text-small': { family: 'text', words: 'a short text' },
	'text-large': { family: 'text', words: 'a long text' },
	'rich-text': { family: 'text', words: 'a rich text' },
	choice: { family: 'choice', list: true, words: 'a choice' },
	number: { family: 'number', words: 'a number' },
	decimal: { family: 'number', words: 'a number' },
	integer: { family: 'number', words: 'a whole number' },
	boolean: { family: 'boolean', words: 'a yes/no' },
	date: { family: 'date', list: true, words: 'a date' },
	'date-time': { family: 'date', list: true, words: 'a date and time' },
	time: { family: 'time', words: 'a time' },
	url: { family: 'url', words: 'a link' },
	phone: { family: 'phone', words: 'a phone number' },
	email: { family: 'email', words: 'an email address' },
	location: { family: 'location', words: 'a location' },
	rating: { family: 'rating', words: 'a rating' },
	'card-lookup': { family: 'lookup', list: true, words: 'an item lookup' },
	measurement: { family: 'measurement', words: 'a measurement' },
	user: { family: 'user', list: true, words: 'a person' },
	currency: { family: 'currency', words: 'an amount of money' },
	color: { family: 'color', words: 'a color' },
	file: { family: 'file', list: true, words: 'a file' },
	confirmations: { family: 'confirmations', words: 'a count of people who said me too' },
	'repeating-group': { family: 'group', words: 'a repeating group' },
};

/** Every data type the table knows, in the platform's order (KNOWN_TYPES). */
var DATA_TYPES = Object.keys(TYPES);

/** What a target of each family takes, for the generic refusal. */
var TAKES = {
	number: 'a number, a rating, a measurement or an amount of money',
	rating: 'a number or another rating',
	measurement: 'a number or a measurement of the same measure',
	currency: 'a number or another amount of money',
	date: 'a date or a date and time',
	choice: 'another choice',
	lookup: 'an item lookup that points at the same card or one beneath it',
	url: 'another link, or a short or long text',
	phone: 'another phone number, or a short or long text',
	email: 'another email address, or a short or long text',
};

// Text sources a link, a phone number or an email address may be read from:
// what a person typed as plain text. Rich text is markdown, not an address.
var PLAIN_TEXT = ['text-small', 'text-large'];

/** The conversions convert.js knows, by name. `copy` is the only direct one. */
var CONVERSIONS = [
	'copy', 'text', 'parse',
	'numberToMeasurement', 'measurementToNumber', 'measurementUnit',
	'numberToCurrency', 'currencyToNumber',
	'dateTimeToDate', 'dateToDateTime',
	'choiceMap',
];

// ---------------------------------------------------------------- helpers

function hasOwn(obj, key) {
	return !!obj && Object.prototype.hasOwnProperty.call(obj, key);
}

function optionsOf(el) {
	return el && el.options && typeof el.options === 'object' && !Array.isArray(el.options) ? el.options : {};
}

/**
 * An element's data type as the platform reads it: an element saved before
 * every element had a type reads as a short text (docs/SCHEMA.md, "Every
 * element must declare a dataType": the editor and every renderer read it so).
 */
function dataTypeOf(el) {
	var type = el && el.dataType;
	return type === undefined || type === null || type === '' ? 'text-small' : String(type);
}

function typeOf(el) {
	var type = dataTypeOf(el);
	return hasOwn(TYPES, type) ? TYPES[type] : null;
}

/** The person's word for an element, in quotes: its label, else its name. */
function quoted(el) {
	var label = el && el.label;
	var text = label && typeof label === 'object' ? label.singular : label;
	if (typeof text !== 'string' || !text.trim()) text = el && el.name;
	return '"' + (typeof text === 'string' && text.trim() ? text.trim() : 'this element') + '"';
}

/** Does the element hold a LIST ("Allow multiple" on a type that has it)? */
function isList(el) {
	var type = typeOf(el);
	return !!(type && type.list && optionsOf(el).allowMultiple === true);
}

function measureOf(el) {
	var measure = optionsOf(el).measure;
	return typeof measure === 'string' && measure.trim() ? measure.trim() : null;
}

/**
 * Every measure a measurement element allows, its primary first (KPR-1085:
 * `measures` counts only as a list naming the primary) -- read by id, as
 * measureOf is, with no registry.
 */
function measuresOf(el) {
	var primary = measureOf(el);
	if (!primary) return [];
	var list = optionsOf(el).measures;
	var out = [primary];
	if (Array.isArray(list) && list.indexOf(primary) !== -1) {
		list.forEach(function (id) {
			if (typeof id === 'string' && id.trim() && out.indexOf(id.trim()) === -1) out.push(id.trim());
		});
	}
	// An element's own units (KPR-1109): each a measure of one unit, its id.
	ownUnitsOf(el).forEach(function (own) { if (out.indexOf(own.value) === -1) out.push(own.value); });
	return out;
}

var OWN_PREFIX = 'own:';
function isOwnUnitId(id) {
	return typeof id === 'string' && id.length > OWN_PREFIX.length && id.slice(0, OWN_PREFIX.length) === OWN_PREFIX;
}

/**
 * ownUnitsOf(el) -> [{ value: id, label: singular, plural }]
 * A measurement element's OWN units (KPR-1109, `options.ownUnits`), in its
 * order, as choicesOf lists choices: the rows a unit map is drawn from.
 */
function ownUnitsOf(el) {
	var raw = optionsOf(el).ownUnits;
	var out = [];
	(Array.isArray(raw) ? raw : []).forEach(function (own) {
		if (!own || typeof own !== 'object' || !isOwnUnitId(own.id) || typeof own.singular !== 'string' || !own.singular.trim()) return;
		for (var i = 0; i < out.length; i++) if (out[i].value === own.id) return;
		var singular = own.singular.trim();
		out.push({ value: own.id, label: singular, plural: typeof own.plural === 'string' && own.plural.trim() ? own.plural.trim() : singular });
	});
	return out;
}

/** Whether `el` has the own unit `own` ({ value: id, label: singular }): the same id, named alike. */
function hasOwnUnit(el, own) {
	var units = ownUnitsOf(el);
	for (var i = 0; i < units.length; i++) {
		if (units[i].value === own.value) return fold(units[i].label) === fold(own.label);
	}
	return false;
}

function measureWords(list) {
	var spelled = list.map(function (id) { return isOwnUnitId(id) ? id.slice(OWN_PREFIX.length).replace(/-/g, ' ') : id.replace(/-/g, ' '); });
	return spelled.length < 2 ? (spelled[0] || '') : spelled.slice(0, -1).join(', ') + ' or ' + spelled[spelled.length - 1];
}

function cardIdOf(value) {
	if (value === undefined || value === null) return null;
	var text = String(value).trim().toLowerCase();
	return text || null;
}

/** keepr writes this element's value: driven, calculated or numbered. */
function isWrittenByKeepr(el) {
	var options = optionsOf(el);
	if (options.drivenFrom) return true;
	return !!(options.sequence && typeof options.sequence === 'object');
}

function toSet(names) {
	var out = {};
	if (!names) return out;
	var list = [];
	if (Array.isArray(names)) list = names;
	else if (typeof names.forEach === 'function') names.forEach(function (name) { list.push(name); });
	list.forEach(function (name) {
		if (typeof name === 'string' && name) out['$' + name] = true;
	});
	return out;
}

function inSet(set, name) {
	return hasOwn(set, '$' + name);
}

// ---------------------------------------------------------------- answers

function direct(list) {
	return { verdict: 'direct', conversion: 'copy', list: list || null, needs: [], reason: null };
}

function converts(conversion, list, needs) {
	return { verdict: 'converts', conversion: conversion, list: list || null, needs: needs || [], reason: null };
}

function refused(reason) {
	return { verdict: 'refused', conversion: null, list: null, needs: [], reason: reason };
}

/** `copy` with a `wrap` still changes the value's shape (one value becomes a list of one). */
function copyOrWrap(list) {
	return list === 'wrap' ? converts('copy', 'wrap') : direct(list);
}

// ------------------------------------------------------------ the pair rules

// One rule per (source family, target family) pair that can map at all; a
// pair with no rule is refused with the generic sentence. Text targets are
// settled before this table is read. Each rule answers
// { conversion, needs } or { reason }.
var RULES = {
	'number>number': function () { return { conversion: 'copy' }; },
	// A rating is a number on a scale: it crosses with the plain numbers (the
	// target's steps and bounds are checked), never with a unit or money.
	'number>rating': function () { return { conversion: 'copy' }; },
	'rating>number': function () { return { conversion: 'copy' }; },
	'number>measurement': function (s, t) {
		var measure = measureOf(t);
		if (!measure) return { reason: quoted(t) + ' has no measure set, so a number has no unit to take.' };
		return { conversion: 'numberToMeasurement', needs: [{ kind: 'unit', of: 'target', measure: measure }] };
	},
	'measurement>number': function (s, t) {
		var measure = measureOf(s);
		if (!measure) return { reason: quoted(s) + ' has no measure set, so there is no unit to read it in.' };
		// KPR-1085: a value in another of its measures has no number in that unit.
		if (measuresOf(s).length > 1) return { reason: quoted(s) + ' holds ' + measureWords(measuresOf(s)) + ', so there is no one unit to read it in as a number.' };
		return { conversion: 'measurementToNumber', needs: [{ kind: 'unit', of: 'source', measure: measure }] };
	},
	'measurement>measurement': function (s, t) {
		var from = measureOf(s);
		var to = measureOf(t);
		if (!from || !to) return { reason: quoted(from ? t : s) + ' has no measure set.' };
		// Several measures (KPR-1085): the target must allow every measure the
		// source may hold, as an amount's target must allow its currencies.
		var fromList = measuresOf(s);
		var toList = measuresOf(t);
		if (fromList.length > 1 || toList.length > 1) {
			var missing = fromList.filter(function (id) { return toList.indexOf(id) === -1; });
			// A source's OWN unit the target lacks (KPR-1109) is mapped, unit by
			// unit, to one the target allows or to nothing (a unit map, as a
			// choice map maps choices); a built-in measure it lacks has no map.
			// The target "has" an own unit only under the same id AND the same
			// name: a rename keeps an id, so the target's own:smidgen may be
			// called "dollop" -- another unit, which a value reaches only by a map.
			var missingMeasures = missing.filter(function (id) { return !isOwnUnitId(id); });
			var missingOwn = ownUnitsOf(s).filter(function (own) { return !hasOwnUnit(t, own); }).map(function (own) { return own.value; });
			if (missingMeasures.length) {
				var fromMeasures = fromList.filter(function (id) { return !isOwnUnitId(id); });
				return { reason: quoted(s) + ' holds ' + measureWords(fromMeasures) + ' and ' + quoted(t) + ' does not allow ' + measureWords(missingMeasures) + '; a measurement maps only to one that allows every measure it holds.' };
			}
			if (missingOwn.length) return { conversion: 'measurementUnit', needs: [{ kind: 'units', of: 'source', units: missingOwn }] };
			var allow = optionsOf(t).units;
			return { conversion: Array.isArray(allow) && allow.length ? 'measurementUnit' : 'copy' };
		}
		if (from !== to) {
			return { reason: quoted(s) + ' measures ' + from.replace(/-/g, ' ') + ' and ' + quoted(t) + ' measures ' + to.replace(/-/g, ' ') + '; a measurement maps only to one of the same measure.' };
		}
		var units = optionsOf(t).units;
		return { conversion: Array.isArray(units) && units.length ? 'measurementUnit' : 'copy' };
	},
	'number>currency': function () { return { conversion: 'numberToCurrency', needs: [{ kind: 'currency', of: 'target' }] }; },
	'currency>number': function () { return { conversion: 'currencyToNumber', needs: [{ kind: 'currency', of: 'source' }] }; },
	'currency>currency': function () { return { conversion: 'copy' }; },
	'currency>measurement': function (s, t) {
		return { reason: quoted(s) + ' is an amount of money and ' + quoted(t) + ' a measurement; money does not become a quantity.' };
	},
	'measurement>currency': function (s, t) {
		return { reason: quoted(s) + ' is a measurement and ' + quoted(t) + ' an amount of money; a quantity does not become money.' };
	},
	'date>date': function (s, t) {
		var from = dataTypeOf(s);
		var to = dataTypeOf(t);
		if (from === to) return { conversion: 'copy' };
		return { conversion: from === 'date-time' ? 'dateTimeToDate' : 'dateToDateTime' };
	},
	'choice>choice': function () { return { conversion: 'choiceMap', needs: [{ kind: 'choices' }] }; },
	'lookup>lookup': function (s, t, ctx) {
		var from = cardIdOf(optionsOf(s).lookupCardId);
		var to = cardIdOf(optionsOf(t).lookupCardId);
		if (!from || !to) return { reason: quoted(from ? t : s) + ' does not say which card it points at.' };
		if (from === to) return { conversion: 'copy' };
		var reaches = ctx && typeof ctx.reaches === 'function' ? ctx.reaches : null;
		if (reaches && reaches(to, from) === true) return { conversion: 'copy' };
		return { reason: quoted(t) + ' points at items of a card that ' + quoted(s) + "'s items may not belong to; it must point at the same card, or a card above it." };
	},
	'text>url': parseFromText,
	'text>phone': parseFromText,
	'text>email': parseFromText,
};

function parseFromText(s, t) {
	if (PLAIN_TEXT.indexOf(dataTypeOf(s)) === -1) {
		return { reason: quoted(s) + ' is a rich text; only a short or long text is read as ' + TYPES[dataTypeOf(t)].words + '.' };
	}
	return { conversion: 'parse' };
}

// Families whose elements map to one of exactly their own type.
var SAME_TYPE_ONLY = ['rating', 'boolean', 'time', 'url', 'phone', 'email', 'location', 'user', 'color', 'file'];

// ---------------------------------------------------------------- classify

/**
 * classify(source, target, ctx) ->
 *   { verdict: 'direct' | 'converts' | 'refused',
 *     conversion: one of CONVERSIONS | null,
 *     list: 'each' | 'wrap' | 'join' | null,
 *     needs: [{ kind: 'unit', of: 'source' | 'target', measure } | { kind: 'currency', of } | { kind: 'choices' }
 *             | { kind: 'units', of: 'source', units: [own unit ids the target lacks] } (KPR-1109)],
 *     reason: a sentence naming both elements | null }
 *
 * `source` and `target` are card-definition elements ({ name, label,
 * dataType, options }). `ctx.reaches(targetCardId, sourceCardId)` answers
 * whether an item lookup pointing at the first card accepts items of the
 * second — true when the second is the first or sits beneath it (an item
 * lookup accepts items of its card and every card beneath it). Without it,
 * only the same card reaches.
 */
function classify(source, target, ctx) {
	if (!source || typeof source !== 'object') return refused('There is no source element to map.');
	if (!target || typeof target !== 'object') return refused('There is no target element to map to.');
	var s = typeOf(source);
	var t = typeOf(target);
	if (!s) return refused(quoted(source) + ' has a data type keepr does not know ("' + dataTypeOf(source) + '").');
	if (!t) return refused(quoted(target) + ' has a data type keepr does not know ("' + dataTypeOf(target) + '").');

	if (s.family === 'group') return refused(quoted(source) + ' is a repeating group; its entries cannot be moved into another element.');
	if (t.family === 'group') return refused(quoted(target) + ' is a repeating group; a value cannot be moved into its entries.');
	// keepr writes a driven, calculated or numbered element itself. offeredTargets
	// never offers one; classify refuses one too, so the move's own gate
	// (convert.js asks this first) holds even for a caller that skipped the offer.
	if (isWrittenByKeepr(target)) {
		return refused(quoted(target) + (optionsOf(target).drivenFrom ? ' is filled in by keepr from other values' : ' is numbered by keepr') + ', so nothing is moved into it.');
	}
	if (s.family === 'confirmations') return refused(quoted(source) + ' counts the people who said me too; keepr keeps that count, so it cannot be moved.');
	if (t.family === 'confirmations') return refused(quoted(target) + ' counts the people who said me too; keepr keeps that count, so nothing can be moved into it.');

	var sourceList = isList(source);
	var targetList = isList(target);

	// Any value reads as text, a list of them joined.
	if (t.family === 'text') {
		if (s.family === 'file') {
			return refused(quoted(source) + ' holds files; files stay on the item as attachments, so map it to a file element or leave it out.');
		}
		if (s.family === 'text') return direct(null);
		return converts('text', sourceList ? 'join' : null);
	}

	if (sourceList && !targetList) {
		return refused(quoted(source) + ' can hold several values and ' + quoted(target) + ' holds one; map it to an element that allows several, or to a text.');
	}
	var list = sourceList ? 'each' : (targetList ? 'wrap' : null);

	var key = s.family + '>' + t.family;
	var answer = null;
	if (hasOwn(RULES, key)) answer = RULES[key](source, target, ctx || {});
	else if (s.family === t.family && SAME_TYPE_ONLY.indexOf(s.family) !== -1) answer = { conversion: 'copy' };

	if (!answer) {
		var takes = hasOwn(TAKES, t.family) ? TAKES[t.family] : 'only another ' + t.words.replace(/^an? /, '');
		return refused(quoted(source) + ' (' + s.words + ') cannot become ' + quoted(target) + ' (' + t.words + '), which takes ' + takes + '.');
	}
	if (answer.reason) return refused(answer.reason);
	if (answer.conversion === 'copy') return copyOrWrap(list);
	return converts(answer.conversion, list, answer.needs);
}

// ------------------------------------------------------- what is offered

function lastByName(elements) {
	var order = [];
	var byName = {};
	(Array.isArray(elements) ? elements : []).forEach(function (el) {
		if (!el || typeof el !== 'object' || typeof el.name !== 'string' || !el.name) return;
		var key = '$' + el.name;
		if (!hasOwn(byName, key)) order.push(key);
		// A descendant card redeclaring an inherited name wins: the merged
		// chain lists ancestors first, so the LAST definition is the card's.
		byName[key] = el;
	});
	return order.map(function (key) { return byName[key]; });
}

/**
 * offeredTargets(elements, { hidden, seeOnly }) -> { offered: [el], withheld: [{ element, reason }] }
 *
 * The target card's elements a person may fill: its merged chain (own,
 * ancestors', element sets'), one definition per name. `hidden` and `seeOnly`
 * are the caller's element names (a Set or a list) hidden from them, and
 * visible but not theirs to change, on the target card. A hidden element is
 * left out of both lists: a reason would name it.
 */
function offeredTargets(elements, opts) {
	var hidden = toSet(opts && opts.hidden);
	var seeOnly = toSet(opts && opts.seeOnly);
	var offered = [];
	var withheld = [];
	lastByName(elements).forEach(function (el) {
		if (inSet(hidden, el.name)) return;
		var reason = targetRefusal(el, inSet(seeOnly, el.name));
		if (reason) withheld.push({ element: el.name, reason: reason });
		else offered.push(el);
	});
	return { offered: offered, withheld: withheld };
}

function targetRefusal(el, seeOnly) {
	var type = typeOf(el);
	if (!type) return quoted(el) + ' has a data type keepr does not know ("' + dataTypeOf(el) + '").';
	var options = optionsOf(el);
	if (options.drivenFrom) return quoted(el) + ' is filled in by keepr from other values, so nothing is moved into it.';
	if (options.sequence && typeof options.sequence === 'object') return quoted(el) + ' is numbered by keepr, so nothing is moved into it.';
	if (type.family === 'confirmations') return quoted(el) + ' counts the people who said me too; keepr keeps that count.';
	if (type.family === 'group') return quoted(el) + ' is a repeating group; a value cannot be moved into its entries.';
	if (seeOnly) return 'You can see ' + quoted(el) + ' but not change it.';
	return null;
}

/**
 * offeredSources(elements, { hidden }) -> [el]
 *
 * The source card's elements a person may map: every element of its merged
 * chain they can read, one definition per name. An element hidden from them
 * is never offered (nor named). A source that maps to nothing (a repeating
 * group) is still offered, so the utility can say why it stays behind.
 */
function offeredSources(elements, opts) {
	var hidden = toSet(opts && opts.hidden);
	return lastByName(elements).filter(function (el) { return !inSet(hidden, el.name); });
}

// ------------------------------------------------------------ choice maps

function choicesOf(el) {
	var raw = optionsOf(el).choices;
	var out = [];
	(Array.isArray(raw) ? raw : []).forEach(function (c) {
		var value = c !== null && typeof c === 'object' ? c.value : c;
		if (value === undefined || value === null || value === '') return;
		var label = c !== null && typeof c === 'object' && typeof c.label === 'string' && c.label.trim() ? c.label : String(value);
		out.push({ value: String(value), label: label });
	});
	return out;
}

function setOwn(obj, key, value) {
	// defineProperty, not `obj[key] = value`: a choice valued "__proto__"
	// would otherwise set the object's prototype instead of a key.
	Object.defineProperty(obj, key, { value: value, enumerable: true, writable: true, configurable: true });
}

function fold(text) {
	return String(text).trim().toLowerCase();
}

/**
 * suggestUnitMap(source, target) -> { sourceUnitId: targetUnitId }
 *
 * A starting unit map for a measurement pair (KPR-1109): each of the source's
 * own units the target lacks goes to the target's own unit with the same name
 * (its singular or plural, ignoring case and surrounding spaces). One with no
 * namesake is left out, for the person to map or leave empty; convert.js
 * counts what is left out. (A unit both name alike under one id needs no map;
 * one id the target names otherwise is not that unit.)
 */
function suggestUnitMap(source, target) {
	var targets = ownUnitsOf(target);
	var out = {};
	ownUnitsOf(source).forEach(function (own) {
		var match = null;
		if (hasOwnUnit(target, own)) return;
		for (var j = 0; j < targets.length && !match; j++) {
			var names = [fold(targets[j].label), fold(targets[j].plural)];
			if (names.indexOf(fold(own.label)) !== -1 || names.indexOf(fold(own.plural)) !== -1) match = targets[j];
		}
		if (match) setOwn(out, own.value, match.value);
	});
	return out;
}

/**
 * suggestChoiceMap(source, target) -> { sourceValue: targetValue }
 *
 * A starting map for a choice -> choice pair: each source choice goes to the
 * target choice with the same stored value, else the one with the same label
 * (ignoring case and surrounding spaces). A choice with neither is left out,
 * for the person to map or leave empty; convert.js counts what is left out.
 */
function suggestChoiceMap(source, target) {
	var targets = choicesOf(target);
	var out = {};
	choicesOf(source).forEach(function (c) {
		var match = null;
		for (var i = 0; i < targets.length && !match; i++) if (targets[i].value === c.value) match = targets[i];
		for (var j = 0; j < targets.length && !match; j++) if (fold(targets[j].label) === fold(c.label)) match = targets[j];
		if (match) setOwn(out, c.value, match.value);
	});
	return out;
}

module.exports = {
	DATA_TYPES: DATA_TYPES,
	CONVERSIONS: CONVERSIONS,
	dataTypeOf: dataTypeOf,
	quoted: quoted,
	isList: isList,
	isWrittenByKeepr: isWrittenByKeepr,
	classify: classify,
	offeredTargets: offeredTargets,
	offeredSources: offeredSources,
	choicesOf: choicesOf,
	suggestChoiceMap: suggestChoiceMap,
	ownUnitsOf: ownUnitsOf,
	hasOwnUnit: hasOwnUnit,
	suggestUnitMap: suggestUnitMap,
};
